import React, {
  useState,
  useEffect,
  useImperativeHandle,
  forwardRef,
} from 'react';
import {
  TableCellsMerge20Regular,
  TableCellsSplit20Regular,
  TableDeleteColumn20Regular,
  TableDeleteRow20Regular,
  TableDismiss20Regular,
  TableStackAbove20Regular,
  TableStackBelow20Regular,
  TableStackLeft20Regular,
  TableStackRight20Regular,
} from '@fluentui/react-icons';
import {
  FormatBold,
  FormatItalic,
  FormatListBulleted,
  FormatListNumbered,
  StrikethroughS,
} from '@mui/icons-material';
import { Box, Divider, IconButton, Popover, Tooltip } from '@mui/material';
import { Extension, Node, mergeAttributes } from '@tiptap/core';
import { Table } from '@tiptap/extension-table';
import TableCell from '@tiptap/extension-table-cell';
import TableHeader from '@tiptap/extension-table-header';
import TableRow from '@tiptap/extension-table-row';
import { useEditor, EditorContent, type Editor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { DIALOGS } from '../../TransportCloudTable.consts';

const Div = Node.create({
  name: 'div',
  group: 'block',
  content: 'block+',
  defining: true,
  parseHTML() {
    return [{ tag: 'div' }];
  },
  renderHTML({ HTMLAttributes }) {
    return ['div', mergeAttributes(HTMLAttributes), 0];
  },
});

type Attrs = Record<string, unknown>;

const passthrough = (name: string) => ({
  default: null as string | null,
  parseHTML: (element: HTMLElement) => element.getAttribute(name),
  renderHTML: (attributes: Attrs) =>
    attributes[name] ? { [name]: attributes[name] } : {},
});

const withoutBackground = (style: string | null): string | null => {
  if (!style) return null;
  const cleaned = style
    .split(';')
    .filter((decl) => !/^\s*background(-color)?\s*:/i.test(decl))
    .join(';')
    .trim();
  return cleaned || null;
};

const KeepHtmlAttributes = Extension.create({
  name: 'keepHtmlAttributes',
  addGlobalAttributes() {
    return [
      {
        types: ['div', 'paragraph', 'heading', 'blockquote'],
        attributes: {
          style: passthrough('style'),
          class: passthrough('class'),
        },
      },
      {
        types: ['table'],
        attributes: {
          style: passthrough('style'),
          border: passthrough('border'),
          width: passthrough('width'),
          cellpadding: passthrough('cellpadding'),
          cellspacing: passthrough('cellspacing'),
        },
      },
      {
        types: ['tableRow'],
        attributes: { style: passthrough('style') },
      },
      {
        types: ['tableHeader'],
        attributes: {
          style: passthrough('style'),
          align: passthrough('align'),
        },
      },
      {
        types: ['tableCell'],
        attributes: {
          style: {
            default: null as string | null,
            parseHTML: (element: HTMLElement) =>
              withoutBackground(element.getAttribute('style')),
            renderHTML: (attributes: Attrs) =>
              attributes.style ? { style: attributes.style } : {},
          },
        },
      },
    ];
  },
});

const CustomTableCell = TableCell.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      backgroundColor: {
        default: null,
        parseHTML: (element) => element.style.backgroundColor || null,
        renderHTML: (attributes) => {
          if (!attributes.backgroundColor) return {};
          return { style: `background-color: ${attributes.backgroundColor}` };
        },
      },
    };
  },
});

const BLOCK_TAGS = new Set([
  'ADDRESS',
  'ARTICLE',
  'ASIDE',
  'BLOCKQUOTE',
  'BODY',
  'DD',
  'DIV',
  'DL',
  'DT',
  'FIELDSET',
  'FIGCAPTION',
  'FIGURE',
  'FOOTER',
  'FORM',
  'H1',
  'H2',
  'H3',
  'H4',
  'H5',
  'H6',
  'HEADER',
  'HR',
  'LI',
  'MAIN',
  'NAV',
  'OL',
  'P',
  'PRE',
  'SECTION',
  'TABLE',
  'TBODY',
  'TD',
  'TFOOT',
  'TH',
  'THEAD',
  'TR',
  'UL',
]);

const isBoundaryElement = (node: ChildNode | null): boolean => {
  if (!node || node.nodeType !== 1) return false;
  const tag = (node as Element).tagName;
  return tag === 'BR' || BLOCK_TAGS.has(tag);
};

const touchesBlockStart = (node: ChildNode): boolean => {
  let current: ChildNode = node;
  for (;;) {
    const prev = current.previousSibling;
    if (prev) return isBoundaryElement(prev);
    const parent = current.parentElement;
    if (!parent) return true;
    if (BLOCK_TAGS.has(parent.tagName)) return true;
    current = parent;
  }
};

const touchesBlockEnd = (node: ChildNode): boolean => {
  let current: ChildNode = node;
  for (;;) {
    const next = current.nextSibling;
    if (next) return isBoundaryElement(next);
    const parent = current.parentElement;
    if (!parent) return true;
    if (BLOCK_TAGS.has(parent.tagName)) return true;
    current = parent;
  }
};

function normalizeWhitespace(root: HTMLElement): void {
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  const textNodes: Text[] = [];
  while (walker.nextNode()) textNodes.push(walker.currentNode as Text);

  textNodes.forEach((textNode) => {
    const parent = textNode.parentElement;
    if (!parent || parent.closest('pre')) return;

    let value = (textNode.nodeValue ?? '').replace(/[\t\n\r ]+/g, ' ');

    if (touchesBlockStart(textNode)) value = value.replace(/^ +/, '');
    if (touchesBlockEnd(textNode)) value = value.replace(/ +$/, '');

    if (value) textNode.nodeValue = value;
    else parent.removeChild(textNode);
  });
}

function htmlToEditorContent(raw: string): string {
  if (!raw) return '';

  const parsed = new DOMParser().parseFromString(raw, 'text/html');
  const body = parsed.body;
  if (!body) return raw;

  normalizeWhitespace(body);

  const first = body.firstElementChild;
  if (
    first?.tagName === 'P' &&
    !first.textContent?.trim() &&
    !first.querySelector('br, img, table')
  ) {
    first.remove();
  }

  return body.innerHTML.trim();
}

const TABLE_STYLE =
  'border-collapse: collapse; font-family: Arial,Helvetica,sans-serif; font-size: 13px; color: #222222; border: 1px solid #cccccc;';
const HEADER_ROW_STYLE = 'background-color: #f0f0f0;';
const TH_STYLE = 'border: 1px solid #cccccc; padding: 6px; text-align: left;';
const TD_STYLE = 'border: 1px solid #cccccc; padding: 6px;';

const LEGACY_LEADING_PARAGRAPH = true;

const parseStyle = (style: string | null): Map<string, string> => {
  const map = new Map<string, string>();
  (style ?? '').split(';').forEach((decl) => {
    const i = decl.indexOf(':');
    if (i === -1) return;
    const prop = decl.slice(0, i).trim().toLowerCase();
    const value = decl.slice(i + 1).trim();
    if (prop && value) map.set(prop, value);
  });
  return map;
};

const formatStyle = (map: Map<string, string>): string => {
  const parts: string[] = [];
  map.forEach((value, prop) => parts.push(`${prop}: ${value};`));
  return parts.join(' ');
};

const mergeStyle = (element: HTMLElement, base: string): void => {
  const merged = parseStyle(base);
  parseStyle(element.getAttribute('style')).forEach((value, prop) =>
    merged.set(prop, value),
  );
  element.setAttribute('style', formatStyle(merged));
};

const setIfMissing = (element: Element, name: string, value: string): void => {
  if (!element.hasAttribute(name)) element.setAttribute(name, value);
};

const promoteHeaderRow = (table: HTMLTableElement): void => {
  if (table.tHead) return;

  const body = table.tBodies[0];
  const firstRow = body?.rows[0];
  if (!firstRow) return;
  if (!Array.from(firstRow.cells).every((cell) => cell.tagName === 'TH'))
    return;

  const thead = document.createElement('thead');
  thead.appendChild(firstRow);
  table.insertBefore(thead, table.firstChild);

  mergeStyle(firstRow, HEADER_ROW_STYLE);

  if (body && body.rows.length === 0) body.remove();
};

const flattenParagraphs = (container: HTMLElement): void => {
  const targets = Array.from(container.querySelectorAll('p')).filter((p) => {
    const parent = p.parentElement;
    return parent === container || parent?.tagName === 'DIV';
  });

  targets.forEach((p) => {
    const parent = p.parentElement;
    if (!parent) return;

    const fragment = document.createDocumentFragment();
    while (p.firstChild) fragment.appendChild(p.firstChild);

    if (!fragment.childNodes.length) {
      fragment.appendChild(document.createElement('br'));
    }
    if (p.nextElementSibling?.tagName === 'P') {
      fragment.appendChild(document.createElement('br'));
    }

    parent.replaceChild(fragment, p);
  });
};

function applyEmailStyles(html: string): string {
  const root = document.createElement('div');
  root.innerHTML = html ?? '';

  const isEmpty =
    !root.textContent?.trim() && !root.querySelector('table, img, br');
  if (isEmpty) return '';

  root.querySelectorAll('table').forEach((table) => {
    table.querySelectorAll('colgroup').forEach((colgroup) => colgroup.remove());

    mergeStyle(table, TABLE_STYLE);
    setIfMissing(table, 'border', '1');
    setIfMissing(table, 'cellpadding', '6');
    setIfMissing(table, 'cellspacing', '0');
    setIfMissing(table, 'width', '100%');

    promoteHeaderRow(table);
  });

  root.querySelectorAll('th').forEach((th) => {
    mergeStyle(th, TH_STYLE);
    setIfMissing(th, 'align', 'left');
  });

  root.querySelectorAll('td').forEach((td) => {
    mergeStyle(td, TD_STYLE);
  });

  root.querySelectorAll('td > p, th > p').forEach((p) => {
    (p as HTMLElement).style.margin = '0';
  });

  root
    .querySelectorAll('[colspan="1"]')
    .forEach((el) => el.removeAttribute('colspan'));
  root
    .querySelectorAll('[rowspan="1"]')
    .forEach((el) => el.removeAttribute('rowspan'));
  root.querySelectorAll('[colwidth], [data-colwidth]').forEach((el) => {
    el.removeAttribute('colwidth');
    el.removeAttribute('data-colwidth');
  });

  flattenParagraphs(root);

  const output = root.innerHTML.replace(/<br\s*\/?>/gi, '<br />').trim();

  return LEGACY_LEADING_PARAGRAPH ? `<p></p>\n${output}` : output;
}

const CELL_BG_COLORS: Array<{ label: string; value: string | null }> = [
  { label: 'None', value: null },
  { label: 'Yellow', value: '#fff9c4' },
  { label: 'Green', value: '#e8f5e9' },
  { label: 'Blue', value: '#e3f2fd' },
  { label: 'Red', value: '#ffebee' },
  { label: 'Purple', value: '#f3e5f5' },
  { label: 'Orange', value: '#fff3e0' },
  { label: 'Gray', value: '#f5f5f5' },
];

const EDITOR_CONTENT_SX = {
  '& .ProseMirror': {
    height: 220,
    overflowY: 'auto',
    padding: '8px 10px',
    outline: 'none',
    fontSize: '14px',
    lineHeight: 1.5,
    '& p': { margin: 0 },
    '& h1': { fontSize: '2em', fontWeight: 'bold', margin: '0.67em 0' },
    '& h2': { fontSize: '1.5em', fontWeight: 'bold', margin: '0.83em 0' },
    '& h3': { fontSize: '1.17em', fontWeight: 'bold', margin: '0 0 1em 0' },
    '& strong': { fontWeight: 'bold' },
    '& em': { fontStyle: 'italic' },
    '& ul': { paddingLeft: '1.5em', listStyleType: 'disc', margin: '1em 0' },
    '& ol': { paddingLeft: '1.5em', listStyleType: 'decimal', margin: '1em 0' },
    '& li > p': { margin: 0 },
    '& br + br.ProseMirror-trailingBreak': { display: 'none' },
    '& table': {
      borderCollapse: 'collapse',
      margin: 0,
      '& td, & th': {
        border: '1px solid #ccc',
        boxSizing: 'border-box',
        minWidth: '1em',
        padding: '4px 8px',
        verticalAlign: 'top',
        position: 'relative',
      },
      '& th': { fontWeight: 600 },
      '& .selectedCell:after': {
        zIndex: 2,
        position: 'absolute',
        content: '""',
        left: 0,
        right: 0,
        top: 0,
        bottom: 0,
        background: 'rgba(66, 153, 225, 0.3)',
        pointerEvents: 'none',
      },
    },
  },
  cursor: 'text',
} as const;

type ToolbarProps = { editor: Editor };

const TextEditorToolbar: React.FC<ToolbarProps> = ({ editor }) => {
  const [colorAnchor, setColorAnchor] = useState<HTMLElement | null>(null);
  const [, forceUpdate] = useState(0);

  useEffect(() => {
    const trigger = () => forceUpdate((n) => n + 1);
    editor.on('selectionUpdate', trigger);
    editor.on('transaction', trigger);
    return () => {
      editor.off('selectionUpdate', trigger);
      editor.off('transaction', trigger);
    };
  }, [editor]);

  const isActive = (name: string, attrs?: Record<string, unknown>) =>
    editor.isActive(name, attrs);

  const iconBtnSx = (active: boolean) => ({
    borderRadius: '6px',
    width: 30,
    height: 28,
    padding: 0,
    color: active ? 'primary.main' : 'text.secondary',
    backgroundColor: active ? 'rgba(0,0,0,0.08)' : 'transparent',
    '&:hover': {
      backgroundColor: active ? 'rgba(0,0,0,0.12)' : 'rgba(0,0,0,0.04)',
    },
  });

  const textBtnSx = (active: boolean, danger = false) => ({
    borderRadius: '6px',
    width: 'auto',
    height: 28,
    padding: '0 6px',
    fontSize: '11px',
    fontWeight: 700,
    fontFamily: 'inherit',
    color: danger ? 'error.main' : active ? 'primary.main' : 'text.secondary',
    backgroundColor: active ? 'rgba(0,0,0,0.08)' : 'transparent',
    '&:hover': {
      backgroundColor: danger
        ? 'rgba(211,47,47,0.08)'
        : active
        ? 'rgba(0,0,0,0.12)'
        : 'rgba(0,0,0,0.04)',
    },
  });

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: 0.25,
        px: 1,
        py: '5px',
        backgroundColor: '#f9fafb',
        borderBottom: '1px solid',
        borderColor: 'divider',
        userSelect: 'none',
      }}
    >
      <Tooltip title={DIALOGS.headingOneButton} arrow>
        <IconButton
          size='small'
          sx={textBtnSx(isActive('heading', { level: 1 }))}
          onClick={() =>
            editor.chain().focus().toggleHeading({ level: 1 }).run()
          }
        >
          H1
        </IconButton>
      </Tooltip>
      <Tooltip title={DIALOGS.headingTwoButton} arrow>
        <IconButton
          size='small'
          sx={textBtnSx(isActive('heading', { level: 2 }))}
          onClick={() =>
            editor.chain().focus().toggleHeading({ level: 2 }).run()
          }
        >
          H2
        </IconButton>
      </Tooltip>
      <Tooltip title={DIALOGS.headingThreeButton} arrow>
        <IconButton
          size='small'
          sx={textBtnSx(isActive('heading', { level: 3 }))}
          onClick={() =>
            editor.chain().focus().toggleHeading({ level: 3 }).run()
          }
        >
          H3
        </IconButton>
      </Tooltip>

      <Divider orientation='vertical' flexItem sx={{ mx: 0.5, my: 0.25 }} />

      <Tooltip title={DIALOGS.boldButton} arrow>
        <IconButton
          size='small'
          sx={iconBtnSx(isActive('bold'))}
          onClick={() => editor.chain().focus().toggleBold().run()}
        >
          <FormatBold sx={{ fontSize: 18 }} />
        </IconButton>
      </Tooltip>
      <Tooltip title={DIALOGS.italicButton} arrow>
        <IconButton
          size='small'
          sx={iconBtnSx(isActive('italic'))}
          onClick={() => editor.chain().focus().toggleItalic().run()}
        >
          <FormatItalic sx={{ fontSize: 18 }} />
        </IconButton>
      </Tooltip>
      <Tooltip title={DIALOGS.strikethroughButton} arrow>
        <IconButton
          size='small'
          sx={iconBtnSx(isActive('strike'))}
          onClick={() => editor.chain().focus().toggleStrike().run()}
        >
          <StrikethroughS sx={{ fontSize: 18 }} />
        </IconButton>
      </Tooltip>

      <Divider orientation='vertical' flexItem sx={{ mx: 0.5, my: 0.25 }} />

      <Tooltip title={DIALOGS.orderedListButton} arrow>
        <IconButton
          size='small'
          sx={iconBtnSx(isActive('orderedList'))}
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
        >
          <FormatListNumbered sx={{ fontSize: 18 }} />
        </IconButton>
      </Tooltip>
      <Tooltip title={DIALOGS.bulletListButton} arrow>
        <IconButton
          size='small'
          sx={iconBtnSx(isActive('bulletList'))}
          onClick={() => editor.chain().focus().toggleBulletList().run()}
        >
          <FormatListBulleted sx={{ fontSize: 18 }} />
        </IconButton>
      </Tooltip>

      {isActive('table') && (
        <>
          <Divider orientation='vertical' flexItem sx={{ mx: 0.5, my: 0.25 }} />

          <Tooltip title={DIALOGS.addRowBeforeButton} arrow>
            <IconButton
              size='small'
              sx={iconBtnSx(false)}
              onClick={() => editor.chain().focus().addRowBefore().run()}
            >
              <TableStackAbove20Regular />
            </IconButton>
          </Tooltip>
          <Tooltip title={DIALOGS.addRowAfterButton} arrow>
            <IconButton
              size='small'
              sx={iconBtnSx(false)}
              onClick={() => editor.chain().focus().addRowAfter().run()}
            >
              <TableStackBelow20Regular />
            </IconButton>
          </Tooltip>
          <Tooltip title={DIALOGS.deleteRowButton} arrow>
            <IconButton
              size='small'
              sx={iconBtnSx(false)}
              onClick={() => editor.chain().focus().deleteRow().run()}
            >
              <TableDeleteRow20Regular />
            </IconButton>
          </Tooltip>

          <Divider orientation='vertical' flexItem sx={{ mx: 0.5, my: 0.25 }} />

          <Tooltip title={DIALOGS.addColumnBeforeButton} arrow>
            <IconButton
              size='small'
              sx={iconBtnSx(false)}
              onClick={() => editor.chain().focus().addColumnBefore().run()}
            >
              <TableStackLeft20Regular />
            </IconButton>
          </Tooltip>
          <Tooltip title={DIALOGS.addColumnAfterButton} arrow>
            <IconButton
              size='small'
              sx={iconBtnSx(false)}
              onClick={() => editor.chain().focus().addColumnAfter().run()}
            >
              <TableStackRight20Regular />
            </IconButton>
          </Tooltip>
          <Tooltip title={DIALOGS.deleteColumnButton} arrow>
            <IconButton
              size='small'
              sx={iconBtnSx(false)}
              onClick={() => editor.chain().focus().deleteColumn().run()}
            >
              <TableDeleteColumn20Regular />
            </IconButton>
          </Tooltip>

          <Divider orientation='vertical' flexItem sx={{ mx: 0.5, my: 0.25 }} />

          <Tooltip title={DIALOGS.mergeCellsButton} arrow>
            <IconButton
              size='small'
              sx={iconBtnSx(false)}
              onClick={() => editor.chain().focus().mergeCells().run()}
            >
              <TableCellsMerge20Regular />
            </IconButton>
          </Tooltip>
          <Tooltip title={DIALOGS.splitCellsButton} arrow>
            <IconButton
              size='small'
              sx={iconBtnSx(false)}
              onClick={() => editor.chain().focus().splitCell().run()}
            >
              <TableCellsSplit20Regular />
            </IconButton>
          </Tooltip>

          <Tooltip title={DIALOGS.cellBackgroundButton} arrow>
            <IconButton
              size='small'
              sx={{ ...textBtnSx(false), px: '4px' }}
              onClick={(e) => setColorAnchor(e.currentTarget)}
            >
              <Box
                sx={{
                  width: 16,
                  height: 16,
                  borderRadius: '3px',
                  backgroundColor:
                    editor.getAttributes('tableCell').backgroundColor ??
                    '#ffffff',
                  border: '1.5px solid rgba(0,0,0,0.25)',
                }}
              />
            </IconButton>
          </Tooltip>
          <Popover
            open={Boolean(colorAnchor)}
            anchorEl={colorAnchor}
            onClose={() => setColorAnchor(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            transformOrigin={{ vertical: 'top', horizontal: 'left' }}
            PaperProps={{ sx: { p: 1 } }}
          >
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(4, 1fr)',
                gap: 0.5,
              }}
            >
              {CELL_BG_COLORS.map(({ label, value }) => (
                <Tooltip title={label} arrow key={label}>
                  <Box
                    component='button'
                    onClick={() => {
                      editor
                        .chain()
                        .focus()
                        .setCellAttribute('backgroundColor', value)
                        .run();
                      setColorAnchor(null);
                    }}
                    sx={{
                      width: 24,
                      height: 24,
                      borderRadius: '4px',
                      backgroundColor: value ?? '#ffffff',
                      border: '1.5px solid rgba(0,0,0,0.15)',
                      cursor: 'pointer',
                      padding: 0,
                      transition: 'transform 0.1s',
                      '&:hover': {
                        borderColor: 'primary.main',
                        transform: 'scale(1.15)',
                      },
                    }}
                  />
                </Tooltip>
              ))}
            </Box>
          </Popover>

          <Divider orientation='vertical' flexItem sx={{ mx: 0.5, my: 0.25 }} />

          <Tooltip title={DIALOGS.deleteTableButton} arrow>
            <IconButton
              size='small'
              sx={{
                ...iconBtnSx(false),
                color: 'error.main',
                '&:hover': { backgroundColor: 'rgba(211,47,47,0.08)' },
              }}
              onClick={() => editor.chain().focus().deleteTable().run()}
            >
              <TableDismiss20Regular />
            </IconButton>
          </Tooltip>
        </>
      )}
    </Box>
  );
};

export type TextEditorHandle = {
  getHTML: () => string;
  setContent: (html: string) => void;
};

type TextEditorProps = {
  initialContent?: string;
};

export const TextEditor = forwardRef<TextEditorHandle, TextEditorProps>(
  ({ initialContent }, ref) => {
    const editor = useEditor({
      extensions: [
        StarterKit,
        Div,
        KeepHtmlAttributes,
        Table.configure({ resizable: false }),
        TableRow,
        TableHeader,
        CustomTableCell,
      ],
      content: '',
    });

    useEffect(() => {
      if (!editor || initialContent === undefined) return;
      editor.commands.setContent(htmlToEditorContent(initialContent));
    }, [editor, initialContent]);

    useImperativeHandle(
      ref,
      () => ({
        getHTML: () => applyEmailStyles(editor?.getHTML() ?? ''),
        setContent: (html: string) => {
          editor?.commands.setContent(htmlToEditorContent(html));
        },
      }),
      [editor],
    );

    if (!editor) return null;

    return (
      <Box
        sx={{
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: '8px',
          overflow: 'hidden',
          transition: 'border-color 0.15s ease',
          '&:focus-within': {
            borderColor: 'primary.main',
            boxShadow: '0 0 0 3px rgba(25,118,210,0.12)',
          },
        }}
      >
        <TextEditorToolbar editor={editor} />
        <Box sx={EDITOR_CONTENT_SX} onClick={() => editor.commands.focus()}>
          <EditorContent editor={editor} />
        </Box>
      </Box>
    );
  },
);
